"use client";

import { useEffect } from "react";
import { useSearchParams } from "next/navigation";
import { type SpecialRow } from "@/data/specials";
import { cn } from "@punchup/utils/cn";
import { getCDNImageURL } from "@punchup/utils/getCDNImageURL";
import { formatClapCount, formatSpecialAge } from "@/utils/formatters";

import ComedianVideoThumbnail from "./ComedianVideoThumbnail";
import { useSpecialReactions } from "@/hooks/specials";
import Link from "next/link";
import { Comedian } from "@punchup/data";
import { UserEvents, trackEvent } from "@punchup/analytics";

interface FeaturedVideoProps {
  comedian: Comedian;
  special: SpecialRow;
}

export default function FeaturedVideo({
  comedian,
  special,
}: FeaturedVideoProps) {
  const searchParams = useSearchParams();
  const { count: clapCount } = useSpecialReactions({ specialId: special.id });

  // Track page load
  useEffect(() => {
    if (comedian.id && special.id) {
      const urlParams: Record<string, string> = {};
      searchParams.forEach((value, key) => {
        urlParams[key] = value;
      });

      trackEvent(UserEvents.LOADED_CONTENT_PAGE, {
        ComedianId: comedian.id,
        SpecialId: special.id,
        Comedian: comedian.display_name,
        "Special Title": special.title,
        url_params: urlParams,
      });
    }
  }, [
    comedian.id,
    comedian.display_name,
    special.id,
    special.title,
    searchParams,
  ]);

  // generate image URL
  const posterImg = special.preview_img
    ? getCDNImageURL(
        special.preview_img,
        "gravity=auto,fit=crop,height=236,width=411"
      )
    : null;

  const handleClick = () => {
    trackEvent(UserEvents.CLICKED_CONTENT_BANNER, {
      ComedianId: comedian.id,
      SpecialId: special.id,
      Comedian: comedian.display_name,
      "Special Title": special.title,
    });

    // this is an old event that was fired upon clicking only the "Watch for Free" button,
    // although we don't have a button like this any more, the card serves the same purpose
    // preserving for backwards compatibilty
    trackEvent(UserEvents.CLICKED_WATCH_FEATURED_SPECIAL, {
      CTAButtonText: "featured-video-card",
      ComedianId: comedian.id,
      SpecialId: special.id,
      SpecialPrice: special.price,
    });
  };

  return (
    <Link
      href={`/${comedian.slug}/${special.slug}`}
      onClick={handleClick}
      className="flex flex-col gap-y-2.5 md:flex-row md:gap-x-[30px] bg-[#F2F2F3] rounded-xl w-full p-3 pb-4 md:p-6"
    >
      {posterImg ? (
        <ComedianVideoThumbnail
          special={special}
          className="w-full h-auto md:w-[unset] md:h-[225px]"
          borderRadius={12}
          withPlayIcon
        />
      ) : (
        <div />
      )}
      <div className="flex flex-1 flex-col justify-center items-start md:pr-20">
        <h2
          className={cn(
            "text-primary-blue font-semibold md:font-bold text-xl md:text-[22px] line-clamp-2 md:line-clamp-none",
            !special.description && "mb-1 md:mb-2"
          )}
        >
          {special.title}
        </h2>
        {special.description && (
          <p className="text-secondary-blue text-sm line-clamp-2 md:line-clamp-3 mt-1.5 md:mt-2.5 mb-2 md:mb-3.5">
            {special.description}
          </p>
        )}
        <div className="flex flex-row flex-wrap items-center gap-x-2 text-sm text-secondary-blue">
          <span>{formatClapCount(clapCount)} claps</span>
          {special.published_at && (
            <>
              <div className="w-1 h-1 rounded-full bg-secondary-blue" />
              <span>{formatSpecialAge(special.published_at)}</span>
            </>
          )}
        </div>
      </div>
    </Link>
  );
}
